import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Printer, Eye } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import { attendeeAPI, eventAPI } from '../api/endpoints';
import { getAttendeeName } from '../utils/attendeeHelpers';
import { BADGE_LAYOUTS } from '../utils/constants';
import { getConsistentPreviewData, getConsistentLogoConfig } from '../utils/badgePreviewData';
import { useToastContext } from '../context/ToastContext';
import { useBadgeSettings } from '../context/BadgeSettingsContext';
import { useEvent } from '../context/EventContext';
import BadgePreviewModal from '../components/print/BadgePreviewModal';
import BadgePreview from '../components/badges/BadgePreview';
import StickerPreview from '../components/stickers/StickerPreview';
import EventSelector from '../components/common/EventSelector';

const PrintStation = () => {
  const toast = useToastContext();
  const {
    defaultTemplate,
    showQR,
    showLogo,
    stickerSize,
    stickerWidth,
    stickerHeight,
    useCustomStickerSize,
    defaultPrintMode,
    loadEventSettings,
    // New sticker customization settings
    stickerNameFontSize,
    stickerPositionFontSize,
    stickerOrgFontSize,
    stickerLineHeight,
    stickerLogoSize,
    stickerShowLogo,
    stickerAutoScaleFont,
    qrPosition,
    stickerNameFontWeight,
    stickerPositionFontWeight,
    stickerOrgFontWeight
  } = useBadgeSettings();
  const { selectedEvent, setSelectedEvent } = useEvent();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [printMode, setPrintMode] = useState(defaultPrintMode); // Use setting default
  const [statusFilter, setStatusFilter] = useState(''); // Add status filter
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewAttendee, setPreviewAttendee] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);
  const [printingAttendee, setPrintingAttendee] = useState(null);

  // Fetch events with attendee counts
  const { data: eventsData } = useQuery({
    queryKey: ['events-with-counts'],
    queryFn: async () => {
      const response = await eventAPI.getAll();
      const events = response.data.events || [];

      // Fetch attendee counts for all events in parallel
      const eventCountPromises = events.map(async (event) => {
        try {
          const attendeesResponse = await attendeeAPI.getAll(event.id);
          return {
            ...event,
            realAttendeeCount: attendeesResponse.data.attendees?.length || 0
          };
        } catch (error) {
          console.warn(`Failed to fetch attendees for event ${event.id}:`, error);
          return {
            ...event,
            realAttendeeCount: event.attendeeCount || 0
          };
        }
      });

      const eventsWithCounts = await Promise.all(eventCountPromises);

      return { events: eventsWithCounts };
    },
  });

  // Fetch attendees (without server-side search)
  const { data: attendeesData, isLoading: attendeesLoading } = useQuery({
    queryKey: ['attendees', selectedEvent],
    queryFn: async () => {
      if (!selectedEvent) return { attendees: [] };
      const response = await attendeeAPI.getAll(selectedEvent);
      return response.data;
    },
    enabled: !!selectedEvent,
  });

  // Helper functions
  const getSelectedEventData = () => {
    const events = eventsData?.events || [];
    return events.find(event => event.id === selectedEvent);
  };


  useEffect(() => {
    const events = eventsData?.events || [];
    if (events.length > 0 && !selectedEvent) {
      setSelectedEvent(events[0].id);
    }
  }, [eventsData, selectedEvent, setSelectedEvent]);

  // Load event settings when event changes
  useEffect(() => {
    if (selectedEvent) {
      loadEventSettings(selectedEvent);
    }
  }, [selectedEvent, loadEventSettings]);

  // Note: Preview is now handled directly by BadgePreview component
  // No need for server-side preview generation

  // Direct print function with immediate window.print()
  const printBadge = async (attendee) => {
    if (!selectedEvent) {
      toast.error('Please select an event before printing.');
      return;
    }

    const inlineComputedStyles = (rootEl) => {
      const toStyleText = (computedStyle) => {
        let cssText = '';
        for (let i = 0; i < computedStyle.length; i++) {
          const prop = computedStyle[i];
          if (prop === 'box-shadow' || prop === 'transition' || prop === 'animation' || prop.includes('transform')) {
            continue;
          }
          const val = computedStyle.getPropertyValue(prop);
          if (!val || val === 'none' || val === '0px none rgb(255, 255, 255)') continue;
          cssText += `${prop}:${val};`;
        }
        return cssText;
      };

      const walk = (node) => {
        if (!node || node.nodeType !== 1) return;
        const el = node;
        const computed = window.getComputedStyle(el);
        el.setAttribute('style', toStyleText(computed));
        el.removeAttribute('class');
        for (const child of Array.from(el.children)) {
          walk(child);
        }
      };

      walk(rootEl);
      return rootEl;
    };

    const createPrintTarget = () => {
      const iframe = document.createElement('iframe');
      iframe.setAttribute('title', 'print-frame');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      document.body.appendChild(iframe);

      const frameWindow = iframe.contentWindow;
      const frameDocument = iframe.contentDocument || frameWindow?.document;

      const cleanup = () => {
        try {
          if (iframe && iframe.parentNode) {
            iframe.parentNode.removeChild(iframe);
          }
        } catch (e) {
          // ignore
        }
      };

      if (!frameWindow || !frameDocument) {
        cleanup();
        return null;
      }

      return {
        targetWindow: frameWindow,
        targetDocument: frameDocument,
        cleanup
      };
    };

    const createPopupTarget = () => {
      const popup = window.open('', '_blank', 'width=800,height=600');
      if (!popup) return null;
      return {
        targetWindow: popup,
        targetDocument: popup.document,
        cleanup: () => {
          // no-op
        }
      };
    };

    // Set loading state
    setIsPrinting(true);
    setPrintingAttendee(attendee.id);

    try {
      // Check in the attendee
      await attendeeAPI.checkIn(selectedEvent, attendee.id);

      const attendeeData = formatAttendeeForBadge(attendee);

      // Get sticker dimensions
      const actualStickerSize = useCustomStickerSize
        ? { width: stickerWidth, height: stickerHeight }
        : getStickerDimensions(stickerSize);

      const printTarget = createPrintTarget() || createPopupTarget();
      if (!printTarget) {
        throw new Error('Unable to open print surface. Please allow popups for this site or try a different browser.');
      }

      const { targetWindow, targetDocument } = printTarget;

      // Show loading content first
      const loadingContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Preparing Print - ${attendeeData.name}</title>
          <style>
            body {
              margin: 0;
              padding: 20px;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
              display: flex;
              flex-direction: column;
              justify-content: center;
              align-items: center;
              min-height: 100vh;
              background: #f8fafc;
            }
            .loader {
              border: 4px solid #e5e7eb;
              border-top: 4px solid #3b82f6;
              border-radius: 50%;
              width: 40px;
              height: 40px;
              animation: spin 1s linear infinite;
              margin-bottom: 20px;
            }
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
            .message {
              color: #374151;
              text-align: center;
            }
          </style>
        </head>
        <body>
          <div class="loader"></div>
          <div class="message">
            <h2>Preparing sticker for printing...</h2>
            <p>Please wait while we generate your sticker</p>
          </div>
        </body>
      </html>
    `;

      targetDocument.open();
      targetDocument.write(loadingContent);
      targetDocument.close();

      // Create temporary container for rendering
      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: ${actualStickerSize.width}px;
      height: ${actualStickerSize.height}px;
      z-index: -9999;
      background: white;
    `;
      document.body.appendChild(tempContainer);

      // Render sticker
      const React = (await import('react')).default;
      const { createRoot } = await import('react-dom/client');
      const root = createRoot(tempContainer);

      if (printMode === 'sticker') {
        root.render(
          React.createElement(StickerPreview, {
            layout: defaultTemplate,
            showQR: showQR,
            qrPosition: qrPosition,
            data: {
              name: attendeeData.name,
              position: attendeeData.title || attendeeData.profession,
              organization: attendeeData.company,
              attendeeId: attendeeData.attendeeId || attendee.id,
              eventId: selectedEvent
            },
            size: stickerSize,
            customWidth: '100%',
            customHeight: '100%',
            colorMode: 'dark',
            nameFontSize: stickerNameFontSize,
            positionFontSize: stickerPositionFontSize,
            orgFontSize: stickerOrgFontSize,
            lineHeight: stickerLineHeight,
            logoSize: stickerLogoSize,
            showLogo: stickerShowLogo,
            autoScaleFont: stickerAutoScaleFont,
            nameFontWeight: stickerNameFontWeight,
            positionFontWeight: stickerPositionFontWeight,
            orgFontWeight: stickerOrgFontWeight
          })
        );
      } else {
        const currentEvent = eventsData?.events?.find(e => e.id === selectedEvent);
        const logoConfig = getConsistentLogoConfig(currentEvent);
        root.render(
          React.createElement(BadgePreview, {
            layout: defaultTemplate,
            showQR: showQR,
            showLogo: showLogo,
            data: attendeeData,
            logoConfig: logoConfig
          })
        );
      }

      // Wait for rendering to complete with requestAnimationFrame for better timing
      await new Promise(resolve => {
        requestAnimationFrame(() => {
          setTimeout(resolve, 500); // Reduced from 1500ms to 500ms
        });
      });

      // For sticker printers, avoid rasterization: inline computed styles and print the DOM.
      // This keeps text + SVG QR crisp (vector) in browser print/PDF drivers.
      let stickerHtml = '';
      let imgData = '';
      if (printMode === 'sticker') {
        const stickerRoot = tempContainer.querySelector('.sticker-preview-container') || tempContainer.firstElementChild;
        if (!stickerRoot) {
          throw new Error('Sticker render failed');
        }
        // Fix: Call inlineComputedStyles on the element while it's still in the DOM
        inlineComputedStyles(stickerRoot);
        const cloned = stickerRoot.cloneNode(true);
        stickerHtml = cloned.outerHTML;
      } else {
        // Badges keep existing raster pipeline for maximum CSS compatibility.
        const html2canvas = (await import('html2canvas')).default;
        const canvas = await html2canvas(tempContainer, {
          backgroundColor: 'white',
          scale: 4,
          useCORS: true,
          allowTaint: false,
          logging: false,
          width: tempContainer.offsetWidth,
          height: tempContainer.offsetHeight,
          foreignObjectRendering: false,
          windowWidth: tempContainer.offsetWidth,
          windowHeight: tempContainer.offsetHeight,
          pixelRatio: window.devicePixelRatio || 1,
          scrollX: 0,
          scrollY: 0,
          imageTimeout: 15000,
        });
        imgData = canvas.toDataURL('image/png', 1.0);
      }

      // Update print window with actual content
      const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Print ${printMode === 'sticker' ? 'Sticker' : 'Badge'} - ${attendeeData.name}</title>
          <style>
            @page {
              margin: 0;
              size: ${(actualStickerSize.width / 96).toFixed(3)}in ${(actualStickerSize.height / 96).toFixed(3)}in;
            }
            html, body {
              margin: 0;
              padding: 0;
              width: 100%;
              background: white;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
              color-adjust: exact;
            }
            .page {
              width: ${(actualStickerSize.width / 96).toFixed(3)}in;
              height: ${(actualStickerSize.height / 96).toFixed(3)}in;
              display: flex;
              align-items: center;
              justify-content: center;
              overflow: hidden;
              box-sizing: border-box;
              position: relative;
            }
            .sticker-wrap {
              width: 100%;
              height: 100%;
              display: flex;
              align-items: center;
              justify-content: center;
              text-align: center;
              box-sizing: border-box;
              padding: 2mm; /* Added safety margin for printing */
            }
            .toolbar {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              display: flex;
              gap: 10px;
              align-items: center;
              justify-content: center;
              padding: 10px;
              background: rgba(255, 255, 255, 0.98);
              border-bottom: 1px solid #e5e7eb;
              z-index: 9999;
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            }
            .toolbar button {
              padding: 10px 14px;
              font-size: 14px;
              border-radius: 8px;
              border: 1px solid #d1d5db;
              background: #111827;
              color: #fff;
              cursor: pointer;
            }
            .toolbar button.secondary {
              background: #6b7280;
            }
            .toolbar .hint {
              font-size: 12px;
              color: #374151;
              margin-left: 8px;
            }
            @media print {
              .toolbar { display: none !important; }
              body { background: white; }
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <button onclick="window.print()">Print</button>
            <button class="secondary" onclick="window.close()">Close</button>
            <span class="hint">Set Scale to 100% for best results.</span>
          </div>
          <div class="page">
            <div class="sticker-wrap">
              ${printMode === 'sticker'
          ? stickerHtml
          : `<img src="${imgData}" alt="Badge" style="width:100%; height:100%; object-fit: contain;" />`
        }
            </div>
          </div>
          <script>
            window.onload = function() {
              setTimeout(function() {
                if (!window.__AUTO_PRINT_DONE__) {
                  window.__AUTO_PRINT_DONE__ = true;
                  window.print();
                }
              }, 300);
            };
          </script>
        </body>
      </html>
    `;

      // Replace content and trigger print
      targetDocument.open();
      targetDocument.write(printContent);
      targetDocument.close();

      try {
        targetWindow.onafterprint = () => {
          try {
            printTarget.cleanup();
          } catch (e) {
            // ignore
          }
        };
      } catch (e) {
        // ignore
      }

      // Cleanup
      root.unmount();
      document.body.removeChild(tempContainer);

      toast.success(`Print ready for ${getAttendeeName(attendee)}!`);

    } catch (error) {
      console.error('Print error:', error);
      toast.error(`Failed to generate sticker: ${error.message}`);
    } finally {
      // Clear loading state
      setIsPrinting(false);
      setPrintingAttendee(null);
    }
  };

  // Frontend-based batch print function using html2canvas
  const printBatchBadges = async () => {
    // Validate that an event is selected
    if (!selectedEvent) {
      toast.error('Please select an event before printing.');
      return;
    }

    if (selectedAttendees.length === 0) {
      toast.warning('No attendees selected for printing.');
      return;
    }

    try {
      toast.info(`Preparing to 
      print ${selectedAttendees.length} ${printMode === 'sticker' ? 'stickers' : 'badges'}...`);

      const inlineComputedStyles = (rootEl) => {
        const toStyleText = (computedStyle) => {
          let cssText = '';
          for (let i = 0; i < computedStyle.length; i++) {
            const prop = computedStyle[i];
            if (prop === 'box-shadow' || prop === 'transition' || prop === 'animation' || prop.includes('transform')) {
              continue;
            }
            const val = computedStyle.getPropertyValue(prop);
            if (!val || val === 'none') continue;
            cssText += `${prop}:${val};`;
          }
          return cssText;
        };

        const walk = (node) => {
          if (!node || node.nodeType !== 1) return;
          const el = node;
          const computed = window.getComputedStyle(el);
          el.setAttribute('style', toStyleText(computed));
          el.removeAttribute('class');
          for (const child of Array.from(el.children)) {
            walk(child);
          }
        };

        walk(rootEl);
        return rootEl;
      };

      const createPrintTarget = () => {
        const iframe = document.createElement('iframe');
        iframe.setAttribute('title', 'print-frame');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.opacity = '0';
        iframe.style.pointerEvents = 'none';
        document.body.appendChild(iframe);

        const frameWindow = iframe.contentWindow;
        const frameDocument = iframe.contentDocument || frameWindow?.document;

        const cleanup = () => {
          try {
            if (iframe && iframe.parentNode) {
              iframe.parentNode.removeChild(iframe);
            }
          } catch (e) {
            // ignore
          }
        };

        if (!frameWindow || !frameDocument) {
          cleanup();
          return null;
        }

        return {
          targetWindow: frameWindow,
          targetDocument: frameDocument,
          cleanup
        };
      };

      const createPopupTarget = () => {
        const popup = window.open('', '_blank', 'width=900,height=700');
        if (!popup) return null;
        return {
          targetWindow: popup,
          targetDocument: popup.document,
          cleanup: () => {
            // no-op
          }
        };
      };

      // Check in all selected attendees
      const checkInErrors = [];
      for (const attendeeId of selectedAttendees) {
        try {
          await attendeeAPI.checkIn(selectedEvent, attendeeId);
        } catch (error) {
          console.log('Check-in error for attendee:', attendeeId, error);
          checkInErrors.push(attendeeId);
        }
      }

      if (checkInErrors.length > 0) {
        toast.warning(`${checkInErrors.length} attendee(s) failed to check in`);
      }

      // Get selected attendee objects
      const selectedAttendeeObjects = attendees.filter(a => selectedAttendees.includes(a.id));

      if (printMode === 'sticker') {
        const actualStickerSize = useCustomStickerSize
          ? { width: stickerWidth, height: stickerHeight }
          : getStickerDimensions(stickerSize);

        const printTarget = createPrintTarget() || createPopupTarget();
        if (!printTarget) {
          toast.error('Unable to open print surface. Please allow popups for this site or try a different browser.');
          return;
        }

        const { targetWindow, targetDocument } = printTarget;

        const React = (await import('react')).default;
        const { createRoot } = await import('react-dom/client');

        const stickerPages = [];
        const generationErrors = [];

        for (let i = 0; i < selectedAttendeeObjects.length; i++) {
          const attendee = selectedAttendeeObjects[i];
          try {
            const attendeeData = formatAttendeeForBadge(attendee);

            const tempContainer = document.createElement('div');
            tempContainer.style.cssText = `
              position: fixed;
              top: -9999px;
              left: -9999px;
              width: ${actualStickerSize.width}px;
              height: ${actualStickerSize.height}px;
              z-index: -9999;
              background: white;
            `;
            document.body.appendChild(tempContainer);

            const root = createRoot(tempContainer);
            root.render(
              React.createElement(StickerPreview, {
                layout: defaultTemplate,
                showQR: showQR,
                qrPosition: qrPosition,
                data: {
                  name: attendeeData.name,
                  position: attendeeData.title || attendeeData.profession,
                  organization: attendeeData.company,
                  attendeeId: attendeeData.attendeeId || attendee.id,
                  eventId: selectedEvent
                },
                size: stickerSize,
                customWidth: '100%',
                customHeight: '100%',
                colorMode: 'dark',
                nameFontSize: stickerNameFontSize,
                positionFontSize: stickerPositionFontSize,
                orgFontSize: stickerOrgFontSize,
                lineHeight: stickerLineHeight,
                logoSize: stickerLogoSize,
                showLogo: stickerShowLogo,
                autoScaleFont: stickerAutoScaleFont,
                nameFontWeight: stickerNameFontWeight,
                positionFontWeight: stickerPositionFontWeight,
                orgFontWeight: stickerOrgFontWeight
              })
            );

            await new Promise(resolve => {
              requestAnimationFrame(() => {
                setTimeout(resolve, 350);
              });
            });

            const stickerRoot = tempContainer.querySelector('.sticker-preview-container') || tempContainer.firstElementChild;
            if (!stickerRoot) {
              throw new Error('Sticker render failed');
            }
            // Fix: Call inlineComputedStyles on the element while it's still in the DOM
            inlineComputedStyles(stickerRoot);
            const cloned = stickerRoot.cloneNode(true);
            stickerPages.push({
              attendeeId: attendee.id,
              name: attendeeData.name,
              html: cloned.outerHTML
            });

            root.unmount();
            document.body.removeChild(tempContainer);
          } catch (error) {
            generationErrors.push({
              attendeeId: attendee.id,
              name: getAttendeeName(attendee),
              error: error.message
            });
          }
        }

        if (stickerPages.length === 0) {
          toast.error('Failed to generate any stickers. Please try again.');
          try { printTarget.cleanup(); } catch (e) { }
          return;
        }

        if (generationErrors.length > 0) {
          toast.warning(`Generated ${stickerPages.length} stickers, but ${generationErrors.length} failed. Check console for details.`);
          console.error('Sticker generation errors:', generationErrors);
        }

        const pageHtml = stickerPages.map(p => (
          `<div class="page">
            <div class="sticker-wrap">${p.html}</div>
          </div>`
        )).join('');

        const printContent = `
          <!DOCTYPE html>
          <html>
            <head>
              <title>Stickers - Batch Print</title>
              <style>
                @page {
                  margin: 0;
                  size: ${(actualStickerSize.width / 96).toFixed(3)}in ${(actualStickerSize.height / 96).toFixed(3)}in;
                }
                html, body {
                  margin: 0;
                  padding: 0;
                  width: 100%;
                  background: white;
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                  color-adjust: exact;
                }
                .toolbar {
                  position: fixed;
                  top: 0;
                  left: 0;
                  right: 0;
                  display: flex;
                  gap: 10px;
                  align-items: center;
                  justify-content: center;
                  padding: 10px;
                  background: rgba(255, 255, 255, 0.98);
                  border-bottom: 1px solid #e5e7eb;
                  z-index: 9999;
                  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
                }
                .toolbar button {
                  padding: 10px 14px;
                  font-size: 14px;
                  border-radius: 8px;
                  border: 1px solid #d1d5db;
                  background: #111827;
                  color: #fff;
                  cursor: pointer;
                }
                .toolbar button.secondary {
                  background: #6b7280;
                }
                .toolbar .hint {
                  font-size: 12px;
                  color: #374151;
                  margin-left: 8px;
                }
                .page {
                  width: ${(actualStickerSize.width / 96).toFixed(3)}in;
                  height: ${(actualStickerSize.height / 96).toFixed(3)}in;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  page-break-after: always;
                  break-after: page;
                  overflow: hidden;
                  box-sizing: border-box;
                  position: relative;
                }
                .page:last-child {
                  page-break-after: avoid;
                  break-after: avoid;
                }
                .sticker-wrap {
                  width: 100%;
                  height: 100%;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  text-align: center;
                  box-sizing: border-box;
                  padding: 2mm; /* Added safety margin for printing */
                }
                @media print {
                  .toolbar { display: none !important; }
                  body { background: white; }
                }
              </style>
            </head>
            <body>
              <div class="toolbar">
                <button onclick="window.print()">Print</button>
                <button class="secondary" onclick="window.close()">Close</button>
                <span class="hint">Tip: set Scale to 100% (disable fit-to-page) for best sticker clarity.</span>
              </div>
              ${pageHtml}
              <script>
                try {
                  window.addEventListener('load', function () {
                    setTimeout(function () {
                      if (!window.__AUTO_PRINT_DONE__) {
                        window.__AUTO_PRINT_DONE__ = true;
                        window.print();
                      }
                    }, 200);
                  });
                } catch (e) {}
              </script>
            </body>
          </html>
        `;

        targetDocument.open();
        targetDocument.write(printContent);
        targetDocument.close();

        try {
          targetWindow.onafterprint = () => {
            try { printTarget.cleanup(); } catch (e) { }
          };
        } catch (e) {
          // ignore
        }

        toast.success(`${stickerPages.length} sticker${stickerPages.length !== 1 ? 's' : ''} ready to print!`);
        setSelectedAttendees([]);
        return;
      }

      let badgeImages = [];
      let generationErrors = [];

      // Generate image for each badge using html2canvas
      for (let i = 0; i < selectedAttendeeObjects.length; i++) {
        const attendee = selectedAttendeeObjects[i];
        console.log(`Generating ${printMode} for attendee ${i + 1}/${selectedAttendeeObjects.length}: ${attendee.full_name || getAttendeeName(attendee)}`);

        try {
          const attendeeData = formatAttendeeForBadge(attendee);

          // Create a temporary badge element (same as individual print)
          const tempContainer = document.createElement('div');
          tempContainer.style.position = 'fixed';
          tempContainer.style.top = '-9999px';
          tempContainer.style.left = '-9999px';
          tempContainer.style.width = '400px';
          tempContainer.style.height = '600px';
          tempContainer.style.zIndex = '-9999';
          tempContainer.className = 'w-full max-w-sm';
          document.body.appendChild(tempContainer);

          // Import React and ReactDOM for rendering
          const React = (await import('react')).default;
          const { createRoot } = await import('react-dom/client');

          const root = createRoot(tempContainer);

          // Wait for React to render the component
          await new Promise((resolve, reject) => {
            const currentEvent = eventsData?.events?.find(e => e.id === selectedEvent);
            const logoConfig = getConsistentLogoConfig(currentEvent);

            try {
              // Render appropriate component based on print mode
              if (printMode === 'sticker') {
                root.render(
                  React.createElement(StickerPreview, {
                    layout: defaultTemplate,
                    showQR: showQR,
                    qrPosition: qrPosition,
                    data: {
                      name: attendeeData.name,
                      position: attendeeData.title || attendeeData.profession,
                      organization: attendeeData.company,
                      attendeeId: attendeeData.attendeeId || attendee.id,
                      eventId: selectedEvent
                    },
                    size: stickerSize,
                    customWidth: useCustomStickerSize ? stickerWidth : null,
                    customHeight: useCustomStickerSize ? stickerHeight : null,
                    nameFontSize: stickerNameFontSize,
                    positionFontSize: stickerPositionFontSize,
                    orgFontSize: stickerOrgFontSize,
                    lineHeight: stickerLineHeight,
                    logoSize: stickerLogoSize,
                    showLogo: stickerShowLogo,
                    autoScaleFont: stickerAutoScaleFont,
                    nameFontWeight: stickerNameFontWeight,
                    positionFontWeight: stickerPositionFontWeight,
                    orgFontWeight: stickerOrgFontWeight
                  })
                );
              } else {
                root.render(
                  React.createElement(BadgePreview, {
                    layout: defaultTemplate,
                    showQR: showQR,
                    showLogo: showLogo,
                    data: attendeeData,
                    logoConfig: logoConfig
                  })
                );
              }

              // Give React time to render with optimized timing
              requestAnimationFrame(() => {
                setTimeout(() => {
                  try {
                    resolve();
                  } catch (error) {
                    reject(error);
                  }
                }, 400); // Reduced from 1200ms to 400ms
              });

            } catch (renderError) {
              root.unmount();
              document.body.removeChild(tempContainer);
              reject(renderError);
            }
          });

          // Capture badge as image using html2canvas with better error handling
          const html2canvas = (await import('html2canvas')).default;

          const canvas = await html2canvas(tempContainer, {
            backgroundColor: '#f8fafc',
            scale: 4, // Increased for maximum quality
            useCORS: true,
            allowTaint: true,
            width: tempContainer.offsetWidth,
            height: tempContainer.offsetHeight,
            logging: false, // Disable console logging
            removeContainer: true, // Let html2canvas handle cleanup
            foreignObjectRendering: false, // Disable foreign object rendering for better compatibility
            windowWidth: tempContainer.offsetWidth,
            windowHeight: tempContainer.offsetHeight,
            imageTimeout: 15000, // Allow time for high-quality rendering
            async: true // Use async rendering for better performance
          });

          const imageDataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality PNG
          badgeImages.push({
            name: attendeeData.name || `Attendee ${attendee.id}`,
            image: imageDataUrl,
            attendeeId: attendee.id
          });

          // Cleanup - html2canvas should have removed the container, but ensure cleanup
          try {
            root.unmount();
            if (document.body.contains(tempContainer)) {
              document.body.removeChild(tempContainer);
            }
          } catch (cleanupError) {
            console.warn('Cleanup error:', cleanupError);
          }

        } catch (error) {
          console.error(`Error generating ${printMode} for attendee ${attendee.id}:`, error);
          generationErrors.push({
            attendeeId: attendee.id,
            name: getAttendeeName(attendee),
            error: error.message
          });

          // Continue with other attendees even if one fails
        }
      }

      if (badgeImages.length === 0) {
        toast.error('Failed to generate any badges. Please try again.');
        return;
      }

      if (generationErrors.length > 0) {
        toast.warning(`Generated ${badgeImages.length} badges, but ${generationErrors.length} failed. Check console for details.`);
        console.error('Badge generation errors:', generationErrors);
      }

      // Create print window with all badge images
      const printWindow = window.open('', '_blank', 'width=800,height=600');

      if (!printWindow) {
        toast.error('Popup blocked. Please allow popups for this site.');
        return;
      }

      const badgeImagesHtml = badgeImages.map(badge =>
        `<div style="page-break-after: always; text-align: center; padding: 20px; margin: 0;">
          <img src="${badge.image}" alt="Badge for ${badge.name}" style="max-width: 100%; height: auto; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1); border-radius: 8px; display: block; margin: 0 auto;" />
        </div>`
      ).join('');

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>${printMode === 'sticker' ? 'Stickers' : 'Badges'} - Batch Print</title>
          <style>
            body { margin: 0; padding: 0; background: white; }
            @media print {
              .no-print { display: none; }
              img { box-shadow: none !important; max-width: 100% !important; height: auto !important; }
              div { page-break-inside: avoid; }
            }
            img { max-width: 100%; height: auto; }
          </style>
        </head>
        <body>
          ${badgeImagesHtml}
          <div class="no-print" style="margin-top: 20px; text-align: center; page-break-before: always; padding: 20px;">
            <button onclick="window.print()" style="padding: 12px 24px; font-size: 16px; background: #3B82F6; color: white; border: none; border-radius: 6px; cursor: pointer; margin-right: 10px;">Print All ${printMode === 'sticker' ? 'Stickers' : 'Badges'}</button>
            <button onclick="window.close()" style="padding: 12px 24px; font-size: 16px; background: #6B7280; color: white; border: none; border-radius: 6px; cursor: pointer;">Close</button>
            <p style="margin-top: 10px; font-size: 14px; color: #6B7280;">${badgeImages.length} ${printMode === 'sticker' ? 'sticker' : 'badge'}${badgeImages.length !== 1 ? 's' : ''} ready to print</p>
          </div>
        </body>
        </html>
      `);

      printWindow.document.close();

      // Auto-trigger print dialog with optimized delay
      setTimeout(() => {
        try {
          printWindow.print();
        } catch (printError) {
          console.error('Print dialog error:', printError);
          toast.warning('Print dialog failed to open. Please use the print button in the popup window.');
        }
      }, 800); // Reduced from 1500ms to 800ms

      toast.success(`${badgeImages.length} ${printMode === 'sticker' ? 'sticker' : 'badge'}${badgeImages.length !== 1 ? 's' : ''} ready to print!`);
      setSelectedAttendees([]);

    } catch (error) {
      console.error('Batch print error:', error);
      toast.error(`Failed to print ${printMode === 'sticker' ? 'stickers' : 'badges'}: ${error.message}`);
    }
  };

  // Helper function to get sticker dimensions based on size
  const getStickerDimensions = (size) => {
    const dimensions = {
      tiny: { width: 96, height: 96 },       // 1" x 1"
      small: { width: 144, height: 96 },     // 1.5" x 1"
      '40x20mm': { width: 151, height: 76 }, // 40mm × 20mm
      '40x30mm': { width: 151, height: 113 }, // 40mm × 30mm
      normal: { width: 192, height: 144 },   // 2" x 1.5"
      medium: { width: 240, height: 144 },   // 2.5" x 1.5"
      '60x40mm': { width: 226, height: 151 }, // 60mm × 40mm
      large: { width: 288, height: 192 },    // 3" x 2"
      '80x50mm': { width: 302, height: 189 } // 80mm × 50mm
    };
    return dimensions[size] || dimensions.normal;
  };

  // Helper function to format attendee data for badge preview
  const formatAttendeeForBadge = (attendee, useConsistentPreview = false) => {
    // If using consistent preview, return the same data as BadgeTemplates
    if (useConsistentPreview) {
      const currentEvent = eventsData?.events?.find(e => e.id === selectedEvent);
      return getConsistentPreviewData(currentEvent?.name);
    }

    // Otherwise use real attendee data
    const name = getAttendeeName(attendee);
    const email = getFieldValue(attendee, 'email');
    const company = getFieldValue(attendee, 'company') || getFieldValue(attendee, 'organization');
    const title = getFieldValue(attendee, 'title') || getFieldValue(attendee, 'job_title') || getFieldValue(attendee, 'position');
    const profession = getFieldValue(attendee, 'profession') || getFieldValue(attendee, 'position');

    // Get event name and logo configuration from the selected event data
    const currentEvent = eventsData?.events?.find(e => e.id === selectedEvent);
    const eventName = currentEvent?.name || attendee.event?.name || 'Event';

    // Generate registration number if enabled
    const registrationNumber = currentEvent?.show_registration_number
      ? (currentEvent?.registration_number_format || 'REG-{id}').replace('{id}', String(attendee.id).padStart(4, '0'))
      : null;

    return {
      name,
      email,
      company,
      title,
      profession,
      eventName,
      registrationNumber,
      attendeeId: attendee.id, // Add the attendee ID for QR code generation
    };
  };

  const handlePreview = (attendee) => {
    // Validate that an event is selected
    if (!selectedEvent) {
      toast.error('Please select an event before previewing.');
      return;
    }

    const attendeeData = formatAttendeeForBadge(attendee);
    setPreviewAttendee({
      id: attendee.id,
      name: attendeeData.name,
      data: attendeeData,
      url: null,
      error: null
    });
    setShowPreviewModal(true);
  };

  const handlePrint = (attendee) => {
    printBadge(attendee);
  };

  const handleBatchPrint = () => {
    printBatchBadges();
  };

  const toggleAttendee = (attendeeId) => {
    setSelectedAttendees(prev =>
      prev.includes(attendeeId)
        ? prev.filter(id => id !== attendeeId)
        : [...prev, attendeeId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedAttendees.length === attendees.length && attendees.length > 0) {
      // Unselect all attendees on current page
      setSelectedAttendees(prev => prev.filter(id => !attendees.some(a => a.id === id)));
    } else {
      // Select all attendees on current page
      const currentPageIds = attendees.map(a => a.id);
      setSelectedAttendees(prev => [...new Set([...prev, ...currentPageIds])]);
    }
  };

  // Helper function to get field value from attendee data
  const getFieldValue = (attendee, fieldName) => {
    if (!attendee.data || !Array.isArray(attendee.data)) return '';
    const field = attendee.data.find(d => d.field?.field_name === fieldName);
    return field?.value || field?.field_value || '';
  };

  // Client-side search filter
  const allAttendees = attendeesData?.attendees || [];

  // Debug logging
  console.log('PrintStation Debug:', {
    selectedEvent,
    attendeesData,
    allAttendees: allAttendees.length,
    attendeesLoading
  });

  const filteredAttendees = allAttendees.filter(attendee => {
    // Status filter
    if (statusFilter && attendee.check_in_status !== statusFilter) {
      return false;
    }

    // Search filter
    if (!searchTerm) return true;

    const search = searchTerm.toLowerCase();
    const name = getAttendeeName(attendee).toLowerCase();
    const email = getFieldValue(attendee, 'email').toLowerCase();
    const phone = getFieldValue(attendee, 'phone').toLowerCase();
    const company = getFieldValue(attendee, 'company').toLowerCase();

    return name.includes(search) ||
      email.includes(search) ||
      phone.includes(search) ||
      company.includes(search);
  });

  // Pagination logic
  const totalItems = filteredAttendees.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const attendees = filteredAttendees.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, selectedEvent]);

  // Get template label for display
  const getTemplateLabel = (templateValue) => {
    const template = BADGE_LAYOUTS.find(t => t.value === templateValue);
    return template ? template.label : templateValue;
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1">
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Print Station</h1>
            <p className="text-gray-600 mt-1 text-sm sm:text-base">
              Print {printMode}s using <span className="font-medium text-primary-600">{getTemplateLabel(defaultTemplate)}</span>
              <span className="hidden sm:inline"> template</span>
            </p>
            <div className="flex flex-wrap gap-1 mt-2">
              {showQR && <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">QR Enabled</span>}
              {printMode === 'badge' && showLogo && <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">Logo Enabled</span>}
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <select
              value={printMode}
              onChange={(e) => setPrintMode(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm w-full sm:w-auto"
            >
              <option value="sticker">Stickers</option>
              <option value="badge">Badges</option>
            </select>
            <Button
              onClick={handleBatchPrint}
              className="gap-2 w-full sm:w-auto"
              disabled={selectedAttendees.length === 0 || !selectedEvent}
            >
              <Printer className="h-5 w-5" />
              <span className="hidden xs:inline">Print Selected ({selectedAttendees.length})</span>
              <span className="xs:hidden">Print ({selectedAttendees.length})</span>
            </Button>
          </div>
        </div>

        {/* Event Selector and Search in Same Row */}
        <Card>
          <Card.Content>
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <EventSelector
                  selectedEvent={selectedEvent}
                  onEventChange={setSelectedEvent}
                  className="w-full"
                  attendeeCount={filteredAttendees.length}
                  eventsWithCounts={eventsData?.events || []}
                />
              </div>
              <div className="flex-1 relative">
                <input
                  type="text"
                  placeholder="Search by name, email, phone..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
                />
                {searchTerm && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-xs text-gray-500 bg-white px-1">
                    {filteredAttendees.length}
                  </div>
                )}
              </div>
            </div>
          </Card.Content>
        </Card>

        {/* Status Tabs */}
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setStatusFilter('')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === ''
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              All
              <span className="ml-2 bg-gray-100 text-gray-900 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {allAttendees.length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('pending')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === 'pending'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Awaiting
              <span className="ml-2 bg-yellow-100 text-yellow-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {allAttendees.filter(a => a.check_in_status === 'pending').length}
              </span>
            </button>
            <button
              onClick={() => setStatusFilter('checked_in')}
              className={`whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm ${statusFilter === 'checked_in'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Printed
              <span className="ml-2 bg-green-100 text-green-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                {allAttendees.filter(a => a.check_in_status === 'checked_in').length}
              </span>
            </button>
          </nav>
        </div>

        {/* Attendees List */}
        <Card>
          <Card.Content>
            {attendeesLoading ? (
              <div className="text-center py-12 text-gray-500">Loading attendees...</div>
            ) : attendees.length > 0 ? (
              <div className="space-y-3">
                {/* Select All */}
                <div className="flex items-center p-2 border-b">
                  <input
                    type="checkbox"
                    checked={selectedAttendees.length === attendees.length && attendees.length > 0}
                    onChange={toggleSelectAll}
                    className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">Select All</label>
                </div>
                {attendees.map((attendee) => (
                  <div
                    key={attendee.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-3 sm:p-4 border border-gray-200 rounded-lg hover:bg-gray-50 gap-3 sm:gap-4"
                  >
                    <div className="flex items-center gap-3 sm:gap-4 flex-1 min-w-0">
                      <input
                        type="checkbox"
                        checked={selectedAttendees.includes(attendee.id)}
                        onChange={() => toggleAttendee(attendee.id)}
                        className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500 flex-shrink-0"
                      />
                      <div className="min-w-0 flex-1">
                        <h3 className="font-medium text-gray-900 truncate">
                          {getAttendeeName(attendee)}
                        </h3>
                        <p className="text-sm text-gray-500 truncate">{attendee.event?.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 w-full sm:w-auto">
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => handlePreview(attendee)}
                        className="gap-2 flex-1 sm:flex-none"
                        disabled={!selectedEvent}
                      >
                        <Eye className="h-4 w-4" />
                        <span className="hidden xs:inline">Preview</span>
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => handlePrint(attendee)}
                        className="gap-2 flex-1 sm:flex-none"
                        disabled={!selectedEvent || (isPrinting && printingAttendee === attendee.id)}
                      >
                        {isPrinting && printingAttendee === attendee.id ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            <span className="hidden xs:inline">Printing...</span>
                          </>
                        ) : (
                          <>
                            <Printer className="h-4 w-4" />
                            <span className="hidden xs:inline">Print</span>
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Printer className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No attendees found for this event.</p>
              </div>
            )}
          </Card.Content>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white px-4 py-3 border border-gray-200 rounded-lg">
            <div className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
              <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
              <span className="font-medium">{totalItems}</span> results
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              <div className="flex items-center gap-1">
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => {
                  // Show first page, last page, current page, and pages around current
                  const showPage = page === 1 || page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1);

                  if (!showPage) {
                    // Show ellipsis
                    if (page === currentPage - 2 || page === currentPage + 2) {
                      return <span key={page} className="px-2 text-gray-400">...</span>;
                    }
                    return null;
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${currentPage === page
                        ? 'bg-primary-600 text-white'
                        : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                        }`}
                    >
                      {page}
                    </button>
                  );
                })}
              </div>

              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <BadgePreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        attendeeName={previewAttendee?.name}
        attendeeData={previewAttendee?.data}
        template={defaultTemplate}
        showQR={showQR}
        showLogo={showLogo}
        logoConfig={getConsistentLogoConfig(getSelectedEventData())}
        previewUrl={previewAttendee?.url}
        isLoading={false}
        error={previewAttendee?.error}
        printMode={printMode}
        eventId={selectedEvent}
        stickerSize={stickerSize}
        stickerWidth={stickerWidth}
        stickerHeight={stickerHeight}
        useCustomStickerSize={useCustomStickerSize}
        onPrint={() => {
          if (previewAttendee) {
            const attendee = attendees.find(a => a.id === previewAttendee.id);
            if (attendee) handlePrint(attendee);
          }
        }}
      />
    </Layout>
  );
};

export default PrintStation;
